import PropTypes from 'prop-types';

import { Grid } from '@mui/material';
import { styled } from '@mui/material/styles';

import SideNavComponent from '../Navigation/SideNavContainer';
import TopNavContainer from '../Navigation/TopNavContainer';
import NotificationsContainer from '../Notification/NotificationsContainer';

import FooterComponent from './FooterComponent';

import { isAdmin, isAuthenticated } from '../../utils';

import useAbortController from '../../hooks/useAbortController';
import useBranchData from '../../hooks/useBranchData';
import useUserInactivity from '../../hooks/useUserInactivity';
import Routes from '../../routes';
import { drawerClosedWidth, drawerOpenedWidth } from '../../staticData';
import ErrorBoundary from './ErrorBoundary';

const ignoredProps = ['drawerWidth', 'isAuthenticated', 'isNavigationShown'];

const StyledMainArea = styled(Grid, {
  shouldForwardProp: (prop) => !ignoredProps.includes(prop),
})(({ theme, drawerWidth, isAuthenticated }) => ({
  position: 'relative',
  minHeight: '100vh',
  marginLeft: drawerWidth,
  width: `calc(100% - ${drawerWidth}px)`,
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
  },
  ...(isAuthenticated && {
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const StyledMainComponent = styled(Grid, {
  shouldForwardProp: (prop) => !ignoredProps.includes(prop),
})(({ theme, isNavigationShown }) => ({
  flex: 1,
  backgroundColor: theme.palette.custom.background,
  position: 'relative',
  paddingBottom: '5rem',
  marginTop: isNavigationShown ? theme.spacing(10) : 0,
  [theme.breakpoints.down('sm')]: {
    marginTop: isNavigationShown ? theme.spacing(9) : 0,
    paddingBottom: '1rem',
  },
}));

const MainComponent = (props) => {
  const { currentUser, isDrawerOpen, isNavigationShown } = props;

  useBranchData();
  useAbortController();
  useUserInactivity();

  const drawerWidth = !isNavigationShown
    ? 0
    : isDrawerOpen
    ? drawerOpenedWidth
    : drawerClosedWidth;

  return (
    <ErrorBoundary>
      <Grid sx={{ width: '100%', height: '100%', position: 'relative' }}>
        {isNavigationShown && <SideNavComponent />}
        <StyledMainArea
          container
          direction="column"
          wrap="nowrap"
          drawerWidth={drawerWidth}
          isAuthenticated={isAuthenticated()}
        >
          {isNavigationShown && <TopNavContainer />}
          {!isAdmin() && isAuthenticated() && <NotificationsContainer />}

          <StyledMainComponent
            container
            id="main_content"
            isNavigationShown={isNavigationShown}
          >
            <Routes user={currentUser} />
          </StyledMainComponent>

          <FooterComponent />
        </StyledMainArea>
      </Grid>
    </ErrorBoundary>
  );
};

MainComponent.propTypes = {
  currentUser: PropTypes.instanceOf(Object).isRequired,
  isDrawerOpen: PropTypes.bool,
  isNavigationShown: PropTypes.bool,
};

export default MainComponent;
